/**
 * lv-sticky-atc.js — barre « Lägg till i varukorgen » collante pour Horizon (Livane).
 *
 * Reprend le comportement de la barre de Fullstack 2.5.0 (code réécrit, rien de copié) :
 *  - visible quand le sélecteur de variantes ET le formulaire d'achat sont hors écran
 *    (au-dessus ou en dessous : mode "fullstack"), ou seulement une fois dépassés (mode "after") ;
 *  - masquée dès que le footer entre dans l'écran ;
 *  - l'ajout passe par le vrai bouton Horizon (clic "puppet", comme la barre native Horizon) :
 *    même variante, même quantité, même tiroir panier, mêmes événements Shopify.
 */
(() => {
  'use strict';

  if (!('customElements' in window) || customElements.get('lv-sticky-atc')) return;

  const EVENT_PRODUCT_SELECT = 'shopify:product:select';
  const EVENT_CART_LINES_UPDATE = 'shopify:cart:lines-update';
  const EVENT_CART_ERROR = 'shopify:cart:error';
  const BUSY_SAFETY_MS = 10000;
  const TYPING_SELECTOR = [
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"])',
    'textarea',
    'select',
    '[contenteditable=""]',
    '[contenteditable="true"]',
  ].join(',');

  const coarsePointer = window.matchMedia('(pointer: coarse)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class LvStickyAtc extends HTMLElement {
    #abort = null;
    #io = null;
    #mo = null;
    #ro = null;
    #watched = new Map();
    #buyButtons = null;
    #footer = null;
    #footerVisible = false;
    #formAbove = false;
    #typing = false;
    #forced = false;
    #visible = false;
    #busyTimer = 0;
    #syncFrame = 0;
    #scrollFrame = 0;
    #userSelected = false;
    #errorAtClick = false;
    // ?variant= présent à l'ARRIVÉE (lien de pub vers une variante) = taille déjà choisie.
    // Horizon réécrit ensuite l'URL à chaque changement de couleur : on ne la relit donc pas.
    #landedWithVariant = (() => {
      try {
        return new URL(window.location.href).searchParams.has('variant');
      } catch (error) {
        return false;
      }
    })();

    connectedCallback() {
      this.#teardown();

      const section = this.closest('.shopify-section');
      const form = this.#productForm();
      const bar = this.#bar();
      if (!section || !form || !bar || !this.#button()) {
        console.warn('[lv-sticky-atc] Formulaire produit introuvable dans la section : barre inactive.');
        return;
      }

      this.#setVisible(false, true);
      this.#settle();
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      const productId = CSS.escape(this.dataset.productId || '');

      // Équivalent du <product-form> observé par Fullstack : sélecteur de variantes + formulaire d'achat.
      this.#buyButtons = form.querySelector('form') || form;
      const picker = section.querySelector(`variant-picker[data-product-id="${productId}"]`);
      this.#footer =
        document.querySelector('.page-wrapper > footer') ||
        document.querySelector('footer') ||
        document.querySelector('.shopify-section-group-footer-group');

      this.#io = new IntersectionObserver((entries) => this.#onIntersect(entries));
      for (const element of [this.#buyButtons, picker]) {
        if (!element) continue;
        this.#watched.set(element, true); // considéré visible jusqu'au premier rappel de l'observer
        this.#io.observe(element);
      }
      if (this.#footer) this.#io.observe(this.#footer);

      // Le vrai bouton reste la référence (dispo, rupture, libellé, erreur) : on recopie son état.
      this.#mo = new MutationObserver(() => this.#scheduleSync());
      this.#mo.observe(form, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });

      // Hauteur exposée en CSS : scroll-padding, décalage éventuel d'une bulle de chat.
      // Desktop (>= 990 px) : la page défile dans .page-wrapper, dont la barre de défilement est au bord droit ;
      // on la laisse libre en décalant la barre collante de sa largeur.
      const mesurer = () => {
        document.documentElement.style.setProperty('--lv-satc-h', `${bar.offsetHeight}px`);
        const pw = document.querySelector('.page-wrapper');
        const sb = pw && window.matchMedia('(min-width: 990px)').matches ? Math.max(0, pw.offsetWidth - pw.clientWidth) : 0;
        document.documentElement.style.setProperty('--lv-satc-sb', `${sb}px`);
      };
      this.#ro = new ResizeObserver(mesurer);
      this.#ro.observe(bar);
      const pageWrapper = document.querySelector('.page-wrapper');
      if (pageWrapper) this.#ro.observe(pageWrapper);
      mesurer();

      this.addEventListener('click', this.#onClick, { signal });
      section.addEventListener(EVENT_PRODUCT_SELECT, this.#onProductSelect, { signal });
      // Choix de taille : un clic sur la taille déjà cochée par Horizon ne déclenche aucun événement de variante,
      // on écoute donc directement le sélecteur principal (clic et clavier).
      section.addEventListener('click', this.#onPickerInput, { signal });
      section.addEventListener('change', this.#onPickerInput, { signal });
      document.addEventListener(EVENT_CART_LINES_UPDATE, this.#onCartLinesUpdate, { signal });
      document.addEventListener(EVENT_CART_ERROR, this.#onCartError, { signal });
      document.addEventListener('focusin', this.#onFocusIn, { signal });
      document.addEventListener('focusout', this.#onFocusOut, { signal });
      window.addEventListener('pageshow', this.#onPageShow, { signal });

      // Mode "after" : un saut de défilement (ancre, restauration) peut passer d'« en dessous » à
      // « au-dessus » sans croiser l'écran, donc sans rappel de l'observer. Écoute en capture :
      // couvre le document (mobile) et .page-wrapper (Horizon desktop >= 990px).
      if (this.dataset.mode === 'after') {
        document.addEventListener('scroll', this.#onScroll, { signal, capture: true, passive: true });
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
        document.addEventListener('shopify:block:deselect', this.#onEditorDeselect, { signal });
      }

      this.#scheduleSync();
    }

    disconnectedCallback() {
      this.#teardown();
      this.#visible = false;
      document.documentElement.classList.remove('lv-satc-visible');
      document.documentElement.style.removeProperty('--lv-satc-h');
      document.documentElement.style.removeProperty('--lv-satc-sb');
    }

    #teardown() {
      if (this.#abort) this.#abort.abort();
      if (this.#io) this.#io.disconnect();
      if (this.#mo) this.#mo.disconnect();
      if (this.#ro) this.#ro.disconnect();
      this.#abort = null;
      this.#io = null;
      this.#mo = null;
      this.#ro = null;
      cancelAnimationFrame(this.#syncFrame);
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = 0;
      clearTimeout(this.#busyTimer);
      this.#watched.clear();
      this.#buyButtons = null;
      this.#footer = null;
      this.#footerVisible = false;
      this.#formAbove = false;
      this.#typing = false;
      this.#forced = false;
    }

    // --- Références ----------------------------------------------------------

    #bar() {
      return this.querySelector('[data-lv-satc-bar]');
    }

    #button() {
      return this.querySelector('[data-lv-satc-button]');
    }

    #productForm() {
      const section = this.closest('.shopify-section');
      const id = this.dataset.productId;
      if (!section || !id) return null;
      return section.querySelector(`product-form-component[data-product-id="${CSS.escape(id)}"]`);
    }

    #mainButton() {
      const form = this.#productForm();
      return form ? form.querySelector('[ref="addToCartButton"]') : null;
    }

    // --- Visibilité (logique Fullstack) --------------------------------------

    #onIntersect(entries) {
      for (const entry of entries) {
        if (entry.target === this.#footer) {
          this.#footerVisible = entry.isIntersecting;
          continue;
        }
        this.#watched.set(entry.target, entry.isIntersecting);
        if (entry.target === this.#buyButtons) {
          this.#formAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        }
      }
      this.#update();
    }

    #onScroll = () => {
      if (this.#scrollFrame) return;
      this.#scrollFrame = requestAnimationFrame(() => {
        this.#scrollFrame = 0;
        if (!this.#buyButtons) return;
        const above = this.#buyButtons.getBoundingClientRect().bottom <= 0;
        if (above !== this.#formAbove) {
          this.#formAbove = above;
          this.#update();
        }
      });
    };

    #update() {
      if (!this.isConnected || !this.#io) return;
      let formOnScreen = false;
      for (const onScreen of this.#watched.values()) {
        if (onScreen) formOnScreen = true;
      }
      const passed = this.dataset.mode === 'after' ? this.#formAbove : true;
      const show = this.#forced || (!formOnScreen && passed && !this.#footerVisible && !this.#typing);
      this.#setVisible(show);
    }

    #setVisible(show, force = false) {
      if (!force && show === this.#visible) return;
      this.#visible = show;
      const bar = this.#bar();
      if (!bar) return;
      if (!show && bar.contains(document.activeElement)) {
        const main = this.#mainButton();
        if (main) main.focus({ preventScroll: true });
      }
      bar.dataset.visible = show ? 'true' : 'false';
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.toggleAttribute('inert', !show);
      document.documentElement.classList.toggle('lv-satc-visible', show);
    }

    // --- Ajout au panier : délégué au vrai bouton Horizon --------------------

    #onClick = (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-lv-satc-button]') : null;
      if (!button || button.disabled || this.#isBusy()) return;

      const main = this.#mainButton();
      const form = main ? main.form : null;
      if (!main || !form) return;

      if (main.disabled || (this.dataset.requireSelection === 'true' && this.#needsSelection())) {
        // Clavier / lecteur d'écran : le focus va sur les tailles (pas sur le bouton d'ajout) avant que la barre se masque
        this.#focusSizeOption();
        this.#scrollToForm();
        return;
      }
      if (!form.checkValidity()) {
        form.reportValidity(); // amène la cliente sur le champ obligatoire
        return;
      }

      this.#errorAtClick = this.#hasFormError();
      this.#setBusy(true);
      main.dataset.puppet = 'true'; // drapeau Horizon : pas d'animation sur le bouton hors écran
      main.click();
    };

    #hasFormError() {
      const form = this.#productForm();
      return Boolean(form && form.querySelector('.product-form-text__error:not(.hidden)'));
    }

    #focusSizeOption() {
      const size = this.dataset.sizeOption;
      const section = this.closest('.shopify-section');
      if (!size || !section) return;
      const picker = section.querySelector(`variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`);
      if (!picker) return;
      const inputs = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).filter(
        (input) => input.dataset.optionName === size
      );
      const target = inputs.find((input) => input.checked) || inputs[0] || picker.querySelector(`select[name="options[${size}]"]`);
      if (target) target.focus({ preventScroll: true });
    }

    #onPickerInput = (event) => {
      const size = this.dataset.sizeOption;
      const target = event.target;
      if (!size || this.#userSelected || !(target instanceof Element)) return;
      const picker = target.closest('variant-picker');
      if (!picker || picker.closest('product-card, dialog')) return;
      if (picker.dataset.productId && picker.dataset.productId !== this.dataset.productId) return;
      const isSizeRadio = target.matches('input[type="radio"]') && target.dataset.optionName === size;
      const isSizeSelect = target.matches('select') && target.name === `options[${size}]`;
      if (!isSizeRadio && !isSizeSelect) return;
      this.#userSelected = true;
      this.#scheduleSync();
    };

    #needsSelection() {
      if (this.#userSelected || !this.dataset.sizeOption || this.dataset.multiVariant !== 'true') return false;
      return !this.#landedWithVariant;
    }

    #scrollToForm() {
      const section = this.closest('.shopify-section');
      const productId = CSS.escape(this.dataset.productId || '');
      const target =
        (section && section.querySelector(`variant-picker[data-product-id="${productId}"]`)) || this.#productForm();
      if (target) target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
    }

    #isBusy() {
      const button = this.#button();
      return Boolean(button && button.getAttribute('aria-busy') === 'true');
    }

    #setBusy(busy) {
      clearTimeout(this.#busyTimer);
      const button = this.#button();
      if (button) button.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) this.#busyTimer = window.setTimeout(() => this.#settle(), BUSY_SAFETY_MS);
    }

    #settle() {
      const main = this.#mainButton();
      if (main) main.dataset.puppet = 'false';
      this.#setBusy(false);
    }

    #fromThisForm(event) {
      const form = this.#productForm();
      return !form || !(event.target instanceof Node) || form.contains(event.target);
    }

    #onCartLinesUpdate = (event) => {
      if (!this.#isBusy() || !this.#fromThisForm(event)) return;
      const promise = event.promise;
      if (promise && typeof promise.then === 'function') {
        promise.then(
          () => this.#settle(),
          () => this.#settle()
        );
      } else {
        this.#settle();
      }
    };

    #onCartError = (event) => {
      if (!this.#isBusy() || !this.#fromThisForm(event)) return;
      this.#settle();
      this.#scrollToForm(); // le message d'erreur Horizon s'affiche sous le vrai bouton
    };

    #onPageShow = (event) => {
      if (event.persisted) this.#settle();
    };

    // --- Variante : prix depuis le HTML serveur, état depuis le vrai bouton --

    #onProductSelect = (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      if (origin && origin.closest('product-card, dialog')) return; // cartes produit, quick-add
      // Le choix de taille est suivi par #onPickerInput : changer seulement la couleur ne vaut pas choix de taille.

      const promise = event.promise;
      if (!promise || typeof promise.then !== 'function') return;

      promise.then(
        (result) => {
          const detail = result && result.detail;
          const productId = this.dataset.productId || '';
          if (!detail || !detail.html) return;
          if (detail.productId && String(detail.productId) !== productId) return;

          const fresh = detail.html.querySelector(
            `lv-sticky-atc[data-product-id="${CSS.escape(productId)}"] [data-lv-satc-inner]`
          );
          const inner = this.querySelector('[data-lv-satc-inner]');
          if (fresh && inner) {
            const wasBusy = this.#isBusy();
            inner.replaceChildren(...Array.from(fresh.childNodes, (node) => document.importNode(node, true)));
            if (wasBusy) {
              const button = this.#button();
              if (button) button.setAttribute('aria-busy', 'true');
            }
          }
          this.#scheduleSync();
        },
        (error) => {
          if (!error || error.name !== 'AbortError') console.warn('[lv-sticky-atc]', error);
        }
      );
    };

    #scheduleSync() {
      cancelAnimationFrame(this.#syncFrame);
      this.#syncFrame = requestAnimationFrame(() => this.#syncFromMain());
    }

    #syncFromMain() {
      const main = this.#mainButton();
      const button = this.#button();
      if (!main || !button) return;

      // Nouvelle erreur de validation Horizon (quantité max, etc.) : aucun événement panier ne suivra.
      if (this.#isBusy() && !this.#errorAtClick && this.#hasFormError()) {
        this.#settle();
        this.#scrollToForm();
      }

      if (button.disabled !== main.disabled) button.disabled = main.disabled;

      const label = this.querySelector('[data-lv-satc-label]');
      const text = Array.from(main.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent || '')
        .join('')
        .trim();
      if (label && text && label.textContent !== text) label.textContent = text;

      this.#applySelectionLabel();
    }

    // Livane : tant que la cliente n'a pas choisi sa taille, le bouton dit « Välj storlek »
    // et un clic l'amène au sélecteur (réglage « Faire choisir la taille avant l'ajout »).
    #applySelectionLabel() {
      const label = this.querySelector('[data-lv-satc-label]');
      const button = this.#button();
      if (!label || !button) return;
      const need =
        this.dataset.requireSelection === 'true' && !button.disabled && Boolean(this.dataset.labelSelect) && this.#needsSelection();
      this.toggleAttribute('data-needs-selection', need);
      if (need) {
        if (label.dataset.lvOriginal === undefined) label.dataset.lvOriginal = label.textContent;
        if (label.textContent !== this.dataset.labelSelect) label.textContent = this.dataset.labelSelect;
      } else if (label.dataset.lvOriginal !== undefined) {
        label.textContent = label.dataset.lvOriginal;
        delete label.dataset.lvOriginal;
      }
    }

    // --- Clavier mobile : la barre s'efface pendant la saisie ----------------

    #onFocusIn = (event) => {
      if (!coarsePointer.matches) return;
      const target = event.target;
      if (!(target instanceof Element) || this.contains(target) || !target.matches(TYPING_SELECTOR)) return;
      if (!this.#typing) {
        this.#typing = true;
        this.#update();
      }
    };

    #onFocusOut = () => {
      if (!this.#typing) return;
      window.setTimeout(() => {
        const active = document.activeElement;
        const typing =
          coarsePointer.matches && active instanceof Element && !this.contains(active) && active.matches(TYPING_SELECTOR);
        if (typing !== this.#typing) {
          this.#typing = typing;
          this.#update();
        }
      }, 0);
    };

    // --- Éditeur de thème : barre forcée visible quand le bloc est sélectionné

    #isOwnEditorEvent(event) {
      return event.target instanceof Node && (event.target === this || this.contains(event.target));
    }

    #onEditorSelect = (event) => {
      if (!this.#isOwnEditorEvent(event)) return;
      this.#forced = true;
      this.#update();
    };

    #onEditorDeselect = (event) => {
      if (!this.#isOwnEditorEvent(event)) return;
      this.#forced = false;
      this.#update();
    };
  }

  customElements.define('lv-sticky-atc', LvStickyAtc);
})();
